import React, { useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config/api';

export default function ParentGpsAttendance({ username, onRecorded }) {
  const [data, setData] = useState(null), [message, setMessage] = useState(''), [busy, setBusy] = useState(false), [failed, setFailed] = useState(false);
  const active = useRef(false), locked = useRef(false);
  const endpoint = `${API_ENDPOINTS.BASE_URL}/api/attendance/parent-gps/${encodeURIComponent(username)}`;
  useEffect(() => {
    active.current = true;
    const refresh = async () => {
      try {
        const response = await fetch(endpoint, { credentials: 'include', cache: 'no-store' });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Unable to load GPS attendance.');
        if (active.current) setData(result);
      } catch (error) { if (active.current) { setMessage(error.message); setFailed(true); } }
    };
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => { active.current = false; clearInterval(timer); };
  }, [endpoint]);
  const mark = async action => {
    if (locked.current) return;
    setFailed(false);
    if (!navigator.geolocation) { setMessage('GPS is unavailable in this browser. Use the school gate PIN instead.'); setFailed(true); return; }
    locked.current = true; setBusy(true); setMessage('Checking your location...');
    try {
      const position = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }));
      if (!active.current) return;
      const response = await fetch(endpoint, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, location: {
        latitude: position.coords.latitude, longitude: position.coords.longitude, accuracy: position.coords.accuracy
      } }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Unable to record attendance.');
      if (!active.current) return;
      setData(current => ({ ...current, attendance: result.attendance }));
      setMessage(`${action === 'pickup' ? 'Pickup' : 'Drop-off'} ${result.alreadyRecorded ? 'already recorded' : 'recorded'}.`);
      onRecorded?.(result.attendance);
    } catch (error) {
      if (active.current) { setFailed(true); setMessage(error.code === 1 ? 'Allow location access to use GPS, or use the school gate PIN.' : error.code === 3 ? 'Location timed out. Try again outdoors or use the school gate PIN.' : error.message || 'Unable to find your location. Please try again.'); }
    } finally { locked.current = false; if (active.current) setBusy(false); }
  };
  const attendance = data?.attendance;
  const time = value => value ? new Date(value).toLocaleTimeString('en-NG', { timeZone: 'Africa/Lagos', hour: '2-digit', minute: '2-digit' }) : '';
  return <section className="mt-4 border-t border-slate-200 pt-4" aria-label={`GPS attendance for ${username}`}>
    <h3 className="font-bold text-slate-900">GPS Drop-off &amp; Pickup</h3>
    <div className="flex flex-wrap gap-3 mt-3">
      <button type="button" className="bg-emerald-700 text-white px-4 py-3 rounded-lg font-semibold disabled:opacity-50" disabled={busy || !data?.settings.enabled || attendance?.dropoff_verified || attendance?.pickup_verified} onClick={() => mark('dropoff')}>{attendance?.dropoff_verified ? 'Drop-off recorded' : 'Drop off by GPS'}</button>
      <button type="button" className="bg-sky-700 text-white px-4 py-3 rounded-lg font-semibold disabled:opacity-50" disabled={busy || !data?.settings.enabled || !attendance?.dropoff_verified || attendance?.pickup_verified} onClick={() => mark('pickup')}>{attendance?.pickup_verified ? 'Pickup recorded' : 'Pick up by GPS'}</button>
    </div>
    {(attendance?.dropoff_verified || attendance?.pickup_verified) && <p className="text-sm text-slate-600 mt-2">{attendance.dropoff_verified && `Drop-off ${time(attendance.dropoff_verified_at)}`}{attendance.pickup_verified && ` / Pickup ${time(attendance.pickup_verified_at)}`}</p>}
    {data && !data.settings.enabled && <p className="text-sm text-amber-800 mt-2">GPS attendance is disabled by the school. Use the gate PIN.</p>}
    {message && <p role={failed ? 'alert' : 'status'} className={`text-sm mt-2 ${failed ? 'text-red-700' : 'text-emerald-800'}`}>{message}</p>}
  </section>;
}
